import type { Dispatch, RefObject, SetStateAction } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CustomIcon } from "../../../../components/reusable/CustomIcon";
import type { Campaign, CampaignDashboardText } from "./CampaignDashboardTypes";
import {
  canViewDraft,
  formatStatusLabel,
  getCampaignSource,
  getCampaignSourceLabel,
  getCampaignStatus,
  getDraftId,
  normalizeBadgeVariant,
  readCampaignField,
} from "./CampaignDashboardUtils";

interface CampaignTableColumnsOptions {
  text: CampaignDashboardText;
  selectAllRef: RefObject<HTMLInputElement>;
  allInScopeSelected: boolean;
  selectableCampaignIds: string[];
  selectedCampaigns: string[];
  setSelectedCampaigns: Dispatch<SetStateAction<string[]>>;
  translateDynamicLabel: (_value?: string | null) => string;
  formatDateTime: (_value: string) => string;
  onOpen: (_campaign: Campaign) => void;
  onView: (_campaign: Campaign) => void;
  onEdit: (_campaign: Campaign) => void;
  onArchive: (_campaign: Campaign) => void;
  onDelete: (_campaign: Campaign) => void;
  onExport: (_campaign: Campaign) => void;
}

export const buildCampaignTableColumns = ({
  text: t,
  selectAllRef,
  allInScopeSelected,
  selectableCampaignIds,
  selectedCampaigns,
  setSelectedCampaigns,
  translateDynamicLabel,
  formatDateTime,
  onOpen,
  onView,
  onEdit,
  onArchive,
  onDelete,
  onExport,
}: CampaignTableColumnsOptions): ColumnsType<Campaign> => [
  {
    title: (
      <input
        ref={selectAllRef}
        type="checkbox"
        checked={allInScopeSelected}
        onChange={e => {
          if (e.target.checked) {
            setSelectedCampaigns(selectableCampaignIds);
          } else {
            setSelectedCampaigns([]);
          }
        }}
      />
    ),
    dataIndex: "select",
    key: "select",
    render: (_: unknown, record: Campaign) => (
      <input
        type="checkbox"
        checked={selectedCampaigns.includes(record.campaign_id)}
        onChange={e => {
          e.stopPropagation();
          if (e.target.checked) {
            setSelectedCampaigns(prev =>
              prev.includes(record.campaign_id) ? prev : [...prev, record.campaign_id]
            );
          } else {
            setSelectedCampaigns(prev => prev.filter(id => id !== record.campaign_id));
          }
        }}
      />
    ),
    width: 50,
  },
  Table.EXPAND_COLUMN,
  {
    title: t.campaignName,
    dataIndex: "campaign_name",
    key: "campaign_name",
    width: 240,
    render: (value: string, record: Campaign) => (
      <span
        className="campaign-name"
        dir="auto"
        style={{ unicodeBidi: "plaintext" }}
        onClick={() => onOpen(record)}
      >
        {translateDynamicLabel(value) || (getDraftId(record) !== null ? t.untitledCampaign : "-")}
      </span>
    ),
  },
  {
    title: t.filterSource,
    key: "source",
    width: 130,
    render: (_: unknown, record: Campaign) => {
      const source = getCampaignSource(record);
      if (!source) {
        return "-";
      }
      return (
        <span
          className={`campaign-source-badge campaign-source-badge--${normalizeBadgeVariant(source)}`}
        >
          {getCampaignSourceLabel(record, t, translateDynamicLabel)}
        </span>
      );
    },
  },
  {
    title: t.sentOn,
    dataIndex: "created_at",
    key: "created_at",
    width: 160,
    render: (value: string) => <div style={{ whiteSpace: "pre-line" }}>{formatDateTime(value)}</div>,
  },
  {
    title: t.scheduledAt,
    dataIndex: "scheduledAt",
    key: "scheduledAt",
    width: 160,
    render: (value: string | null | undefined) => {
      if (!value || value === "-") {
        return "-";
      }
      return <div style={{ whiteSpace: "pre-line" }}>{formatDateTime(value)}</div>;
    },
  },
  {
    title: t.audience,
    dataIndex: "audience",
    key: "audience",
    width: 220,
    render: (value: string) => (
      <span dir="auto" style={{ unicodeBidi: "plaintext" }}>
        {translateDynamicLabel(value) || "-"}
      </span>
    ),
  },
  {
    title: t.filterStatus,
    key: "status",
    width: 120,
    render: (_: unknown, record: Campaign) => {
      const status = getCampaignStatus(record);
      const label = readCampaignField(record, ["statusLabel"]) || status;
      return label ? translateDynamicLabel(label) || formatStatusLabel(label, t) : "-";
    },
  },
  {
    title: t.actions,
    key: "actions",
    width: 160,
    render: (_: unknown, record: Campaign) => {
      const isDraft = getDraftId(record) !== null;
      const viewable = !isDraft || canViewDraft(record);
      return (
        <div className="flex items-center space-x-4" onClick={e => e.stopPropagation()}>
          <CustomIcon
            name="view"
            tooltip={viewable ? t.tooltipView : t.campaignNotGenerated}
            disabled={!viewable}
            onClick={() => onView(record)}
          />
          {isDraft ? (
            <CustomIcon name="edit" tooltip={t.editDraft} onClick={() => onEdit(record)} />
          ) : (
            <CustomIcon
              name={record.status === "archive" ? "unarchive" : "archive"}
              tooltip={record.status === "archive" ? t.tooltipUnarchive : t.tooltipArchive}
              onClick={() => onArchive(record)}
            />
          )}
          <CustomIcon name="delete" tooltip={t.tooltipDelete} onClick={() => onDelete(record)} />
          <CustomIcon name="download" tooltip={t.tooltipDownload} onClick={() => onExport(record)} />
        </div>
      );
    },
  },
];
